import type { CommerceStorefrontAliasResolver } from "@teqtank/commerce-contracts";

export interface PathStorefrontAliasResolverOptions {
    readonly segmentIndex?: number;
    readonly reservedSegments?: readonly string[];
}

export function createPathStorefrontAliasResolver(
    options: PathStorefrontAliasResolverOptions = {},
): CommerceStorefrontAliasResolver {
    const segmentIndex = options.segmentIndex ?? 0;
    const reserved = new Set((options.reservedSegments ?? []).map((value) => normalize(value)));
    return (url) => {
        const segment = url.pathname
            .split("/")
            .filter(Boolean)
            .map((value) => decodeURIComponent(value))[segmentIndex];
        if (!segment || reserved.has(normalize(segment))) return null;
        return segment;
    };
}

export function createQueryStorefrontAliasResolver(
    parameter = "site",
): CommerceStorefrontAliasResolver {
    return (url) => clean(url.searchParams.get(parameter));
}

export interface SubdomainStorefrontAliasResolverOptions {
    readonly baseDomain: string;
    readonly reservedSubdomains?: readonly string[];
}

export function createSubdomainStorefrontAliasResolver(
    options: SubdomainStorefrontAliasResolverOptions,
): CommerceStorefrontAliasResolver {
    const baseDomain = normalize(options.baseDomain);
    const reserved = new Set(
        (options.reservedSubdomains ?? ["www"]).map((value) => normalize(value)),
    );
    return (url) => {
        const host = normalize(url.hostname);
        const suffix = `.${baseDomain}`;
        if (!host.endsWith(suffix)) return null;
        const alias = host.slice(0, -suffix.length).split(".").at(-1) ?? null;
        return alias && !reserved.has(alias) ? alias : null;
    };
}

function clean(value: string | null): string | null {
    const result = value?.trim();
    return result ? result : null;
}

function normalize(value: string): string {
    return value
        .trim()
        .replace(/^\/+|\/+$/gu, "")
        .toLocaleLowerCase();
}
